import { useCallback, useEffect, useState } from "react";
import { OverviewPage } from "@/features/overview/OverviewPage";
import {
  apiClient,
  type MentionSummary,
  type UploadSummary
} from "@/shared/api/api-client";
// Vocabulário de status importado, não redeclarado — antes havia uma
// segunda cópia aqui que podia divergir da de `upload-shared`.
import { ACTIVE_STATUSES } from "@/features/uploads/upload-shared";
import { errorMessage } from "@/shared/api/http";
import { retryPauseFrom, usePolling } from "@/shared/hooks/usePolling";
import { toMention, type Mention } from "@/shared/types/mention";
import { useAppShell } from "@/shell/app-shell-context";
import { useCompanyChecklist } from "@/features/companies/use-company-checklist";
import {
  useAttentionCounts,
  useOverviewAttention
} from "@/features/overview/use-overview-attention";
import { hasRoleAtLeast } from "@/shared/utils/roles";

import { useRequireCompany } from "@/shared/hooks/useRequireCompany";
import { useSummaryDays } from "@/shared/components/SummaryPeriodPicker";
// Amostra que alimenta a LISTA "menções que pedem contexto". Os
// cartões saíram daqui em 2026-08-12: eles eram média e contagem sobre
// estas 50 linhas, o que dava um número diferente do total da tela e
// contava menção sem nota como zero. Agora vêm agregados do servidor,
// sobre uma janela de datas de verdade.
const SAMPLE_SIZE = 50;

export function OverviewRoute() {
  const { token, openMention, profile } = useAppShell();
  const company = useRequireCompany();
  const [mentions, setMentions] = useState<Mention[]>([]);
  const [summary, setSummary] = useState<MentionSummary | null>(null);
  const [uploads, setUploads] = useState<UploadSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Até quando o servidor pediu pra esperar (429): o polling do upload
  // ativo respeita, em vez de bater no próximo tick (2026-10-25).
  const [pausedUntil, setPausedUntil] = useState<number | null>(null);
  // O período dos números (1, 7, 15 ou 30 dias até o último dado), o
  // mesmo do Início — uma escolha só, lembrada no navegador.
  const [days, setDays] = useSummaryDays();

  // A rota busca os próprios dados no contrato real — antes vinham do
  // shell, e `uploads` chegava SEMPRE vazio (o shell parou de buscá-los
  // quando a tela de Uploads virou autossuficiente), então o painel de
  // uploads e os alertas ficavam mudos.
  const load = useCallback(async () => {
    if (!company) return;
    try {
      // `allSettled` e não `all`: com `all` (fail-fast) uma falha do
      // resumo apagaria a tela inteira — lista, uploads e painéis —,
      // enquanto na Home a mesma falha só esconde os cartões. Mesmo
      // erro, duas telas, dois comportamentos opostos.
      const [mentionResult, uploadResult, summaryResult] = await Promise.allSettled([
        apiClient.listMentions(token, { page: 1, size: SAMPLE_SIZE }),
        apiClient.listUploads(token, { page: 1, size: 20 }),
        apiClient.mentionSummary(token, { days })
      ]);
      if (mentionResult.status === "rejected") throw mentionResult.reason;
      if (uploadResult.status === "rejected") throw uploadResult.reason;

      setMentions(mentionResult.value.items.map(toMention));
      setUploads(uploadResult.value.items);
      // O resumo é o único que degrada sozinho: sem ele os cartões
      // mostram "—", e o resto da tela continua útil.
      setSummary(summaryResult.status === "fulfilled" ? summaryResult.value : null);
      setError(null);
    } catch (failure) {
      setPausedUntil(retryPauseFrom(failure));
      // `errorMessage`, como o resto do app: a mensagem do servidor quando
      // é `ApiError`, o texto em português quando não é — até 2026-10-25
      // uma queda de rede mostrava "Failed to fetch" cru (achado 29).
      setError(errorMessage(failure, "Não foi possível carregar os dados da empresa."));
    } finally {
      setLoading(false);
    }
  }, [company, token, days]);

  useEffect(() => { void load(); }, [load]);

  // A lista de configuração é carregada UMA vez ao abrir (sem polling: não
  // muda sozinha) e só some do caminho de quem edita se a pessoa não pode
  // criar canais — a ação exige Especialista, como a aba Canais.
  const checklist = useCompanyChecklist(token, Boolean(company));
  const canEditConfig = profile ? hasRoleAtLeast(profile, "specialist") : false;
  // O ajuste do painel "Pontos de atenção": todo mundo lê, Especialista
  // para cima ajusta (2026-11-01). Também uma vez ao abrir.
  const attention = useOverviewAttention(token, Boolean(company));
  // Os números das linhas ligadas que não vêm no resumo (2026-11-02). O
  // `nonce` é o "Atualizar dados": conta de novo mesmo com a janela igual.
  const [countNonce, setCountNonce] = useState(0);
  const draftsVisible = profile ? hasRoleAtLeast(profile, "technician") : false;
  const attentionCounts = useAttentionCounts({
    token,
    attention: attention.attention,
    summary,
    summaryLoading: loading,
    applicability: {
      showsCena: company?.displays_cena ?? true,
      polarityScale: company?.polarity_scale ?? "four_level",
      draftsVisible
    },
    nonce: countNonce
  });

  const processing = uploads.some(upload => ACTIVE_STATUSES.has(upload.status));
  usePolling({
    enabled: processing,
    onTick: () => void load(),
    pausedUntil,
    signature: uploads
      .map(upload => `${upload.id}:${upload.status}:${upload.processed_rows}`)
      .join(",")
  });

  if (!company) return null;

  return (
    <OverviewPage
      company={company}
      mentions={mentions}
      summary={summary}
      uploads={uploads}
      loading={loading}
      error={error}
      processing={processing}
      // O dashboard é da empresa em que já estamos — não passa mais pela
      // raiz (lá o `X-Company-Id` não seria enviado).
      hrefFor={target => `/empresas/${company.id}/${target}`}
      changeCompanyHref="/"
      settingsHref={`/empresas/${company.id}/configuracoes`}
      openMention={openMention}
      reload={() => {
        void load();
        setCountNonce(current => current + 1);
      }}
      days={days}
      onDaysChange={setDays}
      checklist={checklist.checklist}
      checklistLoading={checklist.loading}
      checklistError={checklist.error}
      onChecklistRetry={() => void checklist.reload()}
      checklistSettingsHref={tab => `/empresas/${company.id}/configuracoes?tab=${tab}`}
      onStandardChannels={canEditConfig ? checklist.createStandardChannels : undefined}
      attention={attention.attention}
      onSaveAttention={canEditConfig ? attention.save : undefined}
      attentionCounts={attentionCounts.counts}
      draftExpiries={attentionCounts.draftExpiries}
      draftsVisible={draftsVisible}
    />
  );
}
